import { Check } from 'lucide-react';

import { cn } from '@/utils/cn';

/**
 * Where someone is in a multi-step flow — an import wizard, the giving flow, a
 * welfare review.
 *
 * Steps are named rather than numbered alone, because "step 3 of 5" tells a user how
 * much is left but not what is coming, and the two together are what makes a long
 * form feel finite.
 */

export interface StepperStep {
  id: string;
  label: string;
  description?: string;
}

export interface StepperProps {
  steps: StepperStep[];
  /** Index of the step being worked on. */
  current: number;
  /** Accessible name for the whole progression. */
  label: string;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

export const Stepper = ({
  steps,
  current,
  label,
  orientation = 'horizontal',
  className,
}: StepperProps) => (
  <ol
    aria-label={label}
    className={cn(
      orientation === 'horizontal'
        ? 'flex items-start gap-2 overflow-x-auto'
        : 'flex flex-col gap-4',
      className,
    )}
  >
    {steps.map((step, index) => {
      const done = index < current;
      const active = index === current;

      return (
        <li
          key={step.id}
          aria-current={active ? 'step' : undefined}
          className={cn(
            'flex min-w-0 gap-2',
            orientation === 'horizontal' ? 'flex-1 items-start' : 'items-start',
          )}
        >
          <span
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-caption font-semibold',
              done && 'border-brand bg-brand text-on-brand',
              active && 'border-brand text-brand',
              !done && !active && 'border-border text-fg-tertiary',
            )}
          >
            {done ? <Check aria-hidden className="h-3.5 w-3.5" /> : index + 1}
          </span>

          <span className="min-w-0 pt-0.5">
            <span
              className={cn(
                'block truncate text-body-sm font-medium',
                active ? 'text-fg' : 'text-fg-secondary',
              )}
            >
              {step.label}
            </span>
            {step.description ? (
              <span className="block truncate text-caption text-fg-tertiary">
                {step.description}
              </span>
            ) : null}
          </span>
        </li>
      );
    })}
  </ol>
);
